import { sortAsc } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { WarningToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { FormExceptionDialog } from 'components/referralForm/dialogs/FormExceptionDialog';
import { useFormResetRef, useInputFocusRef } from 'components/referralForm/FormHelpers';
import { usePlaceOfServiceList } from 'services/APIHooks';
import { PlaceOfServiceCode } from 'services/constants';
import { PreferredBillingTypes, useProductConfig } from 'services/ProductConfigHelpers';
import { nextSection, updateCapturedData } from 'store/referralForm/referralFormSlice';

const FACILITY_OTHER = 'OTHER';

export const PlaceOfServiceAndDelivery = ({ section, isOpen, width }) => {
    const dispatch = useDispatch();
    const { capturedData, activeSection } = useSelector((state) => state.referralForm);
    const placeOfServiceList = usePlaceOfServiceList();
    const productConfig = useProductConfig({
        segmentId: capturedData.segmentId,
        productServiceOid: capturedData.productServiceOid,
        billing: capturedData.billing,
    });

    const placeOfServiceRef = useInputFocusRef(isOpen, activeSection, section);
    const formRef = useFormResetRef(isOpen);

    // Store values to submit for site of care exception
    const [valuesToSubmit, setValuesToSubmit] = useState();

    const facilities = [
        ...(
            productConfig?.facilities?.map((facility) => ({
                name: facility.organization_name,
                npi: facility.facility_npi,
            })) ?? []
        ).sort((a, b) => sortAsc(a.name, b.name)),
        { name: 'Other', npi: FACILITY_OTHER },
    ];

    /** Only show facility dropdown if facility is selected, preferred billing type of 'Medical', and facility is not in list of approved sites of care  */
    const showFacilityDropdown = (placeOfService) =>
        placeOfService === PlaceOfServiceCode.FACILITY &&
        productConfig?.preferredBillingType === PreferredBillingTypes.MEDICAL &&
        !productConfig?.validSitesOfCare?.includes(PlaceOfServiceCode.FACILITY);

    const onSubmit = (values, siteOfCareException) => {
        if (siteOfCareException) {
            WarningToast({ message: 'Site of care exception will be sent for review.' });
        }
        dispatch(
            updateCapturedData({
                ...values,
                siteOfCareException,
            }),
        );
        dispatch(nextSection(section, !!siteOfCareException));
    };

    return (
        <>
            <FormExceptionDialog
                exceptionType={FormExceptionDialog.ExceptionTypes.PLACE_OF_SERVICE}
                isOpen={!!valuesToSubmit}
                onSubmit={(exception) => onSubmit(valuesToSubmit, exception)}
                onSecondaryButtonClick={() => setValuesToSubmit()}
            />
            <Formik
                initialValues={capturedData}
                enableReinitialize
                validationSchema={Yup.object({
                    placeOfService: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    facilityNpi: Yup.string().when('placeOfService', {
                        is: (placeOfService) => showFacilityDropdown(placeOfService),
                        then: (schema) =>
                            schema.required(validationStrings.requiredField.errorMessage),
                    }),
                    needsByDate: DateInputFormik.validator()
                        .required(validationStrings.requiredField.errorMessage)
                        .min(dayjs().startOf('day').toDate(), 'Date cannot be in the past.'),
                })}
                onSubmit={(values, { setSubmitting }) => {
                    // Check if place of service is valid or if Other is selected for facility
                    if (
                        productConfig.validSitesOfCare.includes(values.placeOfService) ||
                        (showFacilityDropdown(values.placeOfService) &&
                            values.facilityNpi !== FACILITY_OTHER)
                    ) {
                        onSubmit(values);
                    } else {
                        setValuesToSubmit(values);
                    }
                    setSubmitting(false);
                }}
                innerRef={formRef}
            >
                {(formik) => (
                    <Form style={{ width }} autoComplete="off">
                        <SuggestSingleSelectFormik
                            label="Place Of Service"
                            id="placeOfService"
                            required
                            inputRef={placeOfServiceRef}
                            isLoading={!placeOfServiceList}
                            values={placeOfServiceList}
                            onChange={(newValue) =>
                                formik.setValues({
                                    ...formik.values,
                                    placeOfService: newValue?.code,
                                    facilityNpi: undefined,
                                })
                            }
                            selectedItem={placeOfServiceList?.find(
                                (option) => option.code === formik.values.placeOfService,
                            )}
                        />
                        {showFacilityDropdown(formik.values.placeOfService) && (
                            <SuggestSingleSelectFormik
                                label="Facility"
                                id="facilityNpi"
                                required
                                values={facilities}
                                onChange={(newValue) =>
                                    formik.setFieldValue('facilityNpi', newValue?.npi)
                                }
                                selectedItem={facilities.find(
                                    (option) => option.npi === formik.values.facilityNpi,
                                )}
                            />
                        )}
                        <DateInputFormik
                            label="Needs By Date"
                            id="needsByDate"
                            required
                            minDate={dayjs().startOf('day').toDate()} // Prevent selecting date in the past
                        />
                        <Button
                            intent={Button.Intent.PRIMARY}
                            label="Continue"
                            type="submit"
                            // Ensure productConfig is loaded before submit is available
                            disabled={!(productConfig && formik.isValid && formik.dirty)}
                        />
                    </Form>
                )}
            </Formik>
        </>
    );
};

PlaceOfServiceAndDelivery.propTypes = {
    section: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
    width: PropTypes.string.isRequired,
};
